import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AlertController, IonicModule, ModalController, NavController } from '@ionic/angular';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { ChannelSelectorModalComponent } from 'src/app/components/channel-selector/channel-selector-modal/channel-selector-modal.component';
import { ChannelSelectorModule } from 'src/app/components/channel-selector/channel-selector.module';
import { CustomFieldsModule } from 'src/app/components/custom-fields/custom-fields.module';
import { ResponseInputModule } from 'src/app/components/custom-fields/response-input/response-input.module';
import { ImageModalComponent } from 'src/app/components/image-modal/image-modal.component';
import { SupportStepsModule } from 'src/app/components/support-steps/support-steps.module';
import { INBOX_TYPES, INTEGRATIONS, routes } from 'src/app/constants';
import { fileNameFromImageModalUpload, generateCustomFieldsArray } from 'src/app/utils/utils';
import { SlackChannel, Tag } from 'src/models';
import { Account } from 'src/models/accounts';
import { Entity, ENTITY_TYPES } from 'src/models/entities';
import { AccountsService } from 'src/services/accounts.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { UploadService } from 'src/services/upload.service';

@Component({
  standalone: true,
  selector: 'app-edit-account',
  imports: [
    CommonModule,
    IonicModule,
    FormsModule,
    NgxTippyModule,
    CustomFieldsModule,
    ResponseInputModule,
    ChannelSelectorModule,
    SupportStepsModule,
  ],
  templateUrl: './edit-account.component.html',
  styleUrls: ['./edit-account.component.scss'],
})
export class EditAccountComponent implements OnInit {
  @Input() id?: string;

  @ViewChild('nameInput', { static: false }) nameInput;

  isLoading = false;

  account?: Account;

  name?: string;
  primarySupportAssigneeId?: string;
  primarySupportAssigneeType?: string;

  primarySupportAssignee?: Partial<Entity>;

  secondarySupportAssigneeId?: string;
  secondarySupportAssigneeType?: string;

  secondarySupportAssignee?: Partial<Entity>;

  imageUrl?: string;
  emailsAndDomains?: string[];
  tags?: Tag[];
  slackTeamIds?: string[];
  slackChannelIds?: string[];

  slackChannels: SlackChannel[] = [];
  slackTeams: Partial<{ id: string; name: string; icon: string }>[] = [];

  customFieldOptions: any[];
  customFieldValues: any[];
  supportSteps: any[] = [];
  enableAccountSLA: boolean = false;

  isShowingCustomFieldsModal = false;
  isSavingCustomFields = false;

  emailDomainToAdd: string;

  connectedCrm?: string;
  externalCrmMetadata?: any;

  constructor(
    private accountsService: AccountsService,
    private modalCtrl: ModalController,
    private msgSrvc: MessageService,
    private alertCtrl: AlertController,
    private searchablePopoverService: SearchablePopoverService,
    public authService: AuthService,
    private uploadService: UploadService,
    private navCtrl: NavController,
  ) {}

  async ngOnInit() {
    this.isLoading = true;

    this.customFieldOptions = this.authService.tenant.customCustomerFields ?? [];

    if (this.id) {
      await this.loadAccount();
    } else {
      this.customFieldValues = generateCustomFieldsArray(this.customFieldOptions, [], {});
    }

    if (this.authService.tenant.integrations[INTEGRATIONS.salesforce.key]?.enabled) {
      this.connectedCrm = 'Salesforce';
    } else if (this.authService.tenant.integrations[INTEGRATIONS.hubspot.key]?.enabled) {
      this.connectedCrm = 'Hubspot';
    }

    setTimeout(() => this.nameInput.setFocus(), 500);

    this.isLoading = false;
  }

  async loadAccount() {
    this.account = await this.accountsService.getById(this.id);

    this.name = this.account.name;
    this.imageUrl = this.account.imageUrl;
    this.emailsAndDomains = this.account.emailsAndDomains;
    this.tags = this.account.tags;
    this.slackTeamIds = this.account.slackTeamIds;
    this.slackTeams = this.account.slackTeams ?? [];
    this.slackChannelIds = this.account.slackChannelIds;
    this.slackChannels = this.account.slackChannels ?? [];
    this.primarySupportAssigneeId = this.account.primarySupportAssigneeId;
    this.primarySupportAssigneeType = this.account.primarySupportAssigneeType;
    this.secondarySupportAssigneeId = this.account.secondarySupportAssigneeId;
    this.secondarySupportAssigneeType = this.account.secondarySupportAssigneeType;
    this.primarySupportAssignee = this.account.primarySupportAssignee;
    this.secondarySupportAssignee = this.account.secondarySupportAssignee;
    this.emailsAndDomains = this.account.emailsAndDomains;
    this.externalCrmMetadata = this.account.externalCrmMetadata;
    this.supportSteps = this.account.supportSteps ?? [];
    this.enableAccountSLA = this.supportSteps.length > 0;

    this.customFieldValues = generateCustomFieldsArray(
      this.customFieldOptions ?? [],
      this.account.customFields ?? [],
      {},
    );
  }

  async submit() {
    // Add any text they had but had not hit "enter" on yet
    this.addDomain();

    this.isLoading = true;
    try {
      // Clean up domains
      const emailsAndDomains = this.emailsAndDomains?.length
        ? this.emailsAndDomains.map((domain) => domain.toLowerCase().trim())
        : [];

      if (this.id) {
        await this.accountsService.updatePartial(this.id, {
          name: this.name,
          primarySupportAssigneeId: this.primarySupportAssigneeId,
          primarySupportAssigneeType: this.primarySupportAssigneeType,
          secondarySupportAssigneeId: this.secondarySupportAssigneeId,
          secondarySupportAssigneeType: this.secondarySupportAssigneeType,
          customFields: this.customFieldValues,
          emailsAndDomains,
          slackTeamIds: this.slackTeams.map((team) => team.id),
          slackChannelIds: this.slackChannels.map((channel) => channel.id),
          imageUrl: this.imageUrl,
          supportSteps: this.enableAccountSLA ? this.supportSteps : [],
        });

        this.msgSrvc.show('Account updated successfully!');
      } else {
        await this.accountsService.create({
          name: this.name,
          primarySupportAssigneeId: this.primarySupportAssigneeId,
          primarySupportAssigneeType: this.primarySupportAssigneeType,
          secondarySupportAssigneeId: this.secondarySupportAssigneeId,
          secondarySupportAssigneeType: this.secondarySupportAssigneeType,
          customFields: this.customFieldValues,
          emailsAndDomains,
          slackTeamIds: this.slackTeams.map((team) => team.id),
          slackChannelIds: this.slackChannels.map((channel) => channel.id),
          imageUrl: this.imageUrl,
          supportSteps: this.enableAccountSLA ? this.supportSteps : [],
        });

        this.msgSrvc.show('Account created successfully!');
      }

      this.dismiss();
    } catch (error) {
      this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  async delete() {
    const alert = await this.alertCtrl.create({
      header: 'Delete account?',
      subHeader: `This can't be undone.`,
      buttons: [
        {
          text: 'Nevermind',
        },
        {
          text: 'Delete',
          handler: async () => {
            this.isLoading = true;

            try {
              await this.accountsService.delete(this.id);

              this.dismiss();
            } catch (error) {
              this.msgSrvc.showError(error);
            } finally {
              this.isLoading = false;
            }
          },
        },
      ],
    });
    alert.present();
  }

  async openChannelSelector() {
    const modal = await this.modalCtrl.create({
      component: ChannelSelectorModalComponent,
      componentProps: {
        defaultInternalOnly: false,
        defaultExternalOnly: false,
        hideChannelTypeFilter: false,
        hideSecondaryTeams: false,
      },
    });

    modal.present();

    modal.onDidDismiss().then(({ data }: any) => {
      if (data?.channel && !this.slackChannels.find((c) => c.id === data.channel.id)) {
        this.slackChannels.push(data.channel);
      }
    });
  }

  removeChannel(index: number) {
    this.slackChannels.splice(index, 1);
  }

  showAssigneeOptions(isPrimary: boolean) {
    this.searchablePopoverService.assignees({
      event,
      selectedId: isPrimary ? this.primarySupportAssigneeId : this.secondarySupportAssigneeId,
      showPrimaryRepOption: false,
      showCreatedByUserOption: false,
      showNoneOption: true,
      callback: (id: string, _name: string, entity: Entity) => {
        if (isPrimary) {
          if (this.primarySupportAssigneeId === id) {
            this.primarySupportAssigneeId = null;
            this.primarySupportAssigneeType = null;
            this.primarySupportAssignee = null;
          } else {
            this.primarySupportAssigneeId = id;
            this.primarySupportAssigneeType =
              entity.type === ENTITY_TYPES.USER ? ENTITY_TYPES.USER : ENTITY_TYPES.USER_GROUP;
            this.primarySupportAssignee = entity;
          }
        } else {
          if (this.secondarySupportAssigneeId === id) {
            this.secondarySupportAssigneeId = null;
            this.secondarySupportAssigneeType = null;
            this.secondarySupportAssignee = null;
          } else {
            this.secondarySupportAssigneeId = id;
            this.secondarySupportAssigneeType =
              entity.type === ENTITY_TYPES.USER ? ENTITY_TYPES.USER : ENTITY_TYPES.USER_GROUP;
            this.secondarySupportAssignee = entity;
          }
        }
      },
    });
  }

  manageCustomFields() {
    this.isShowingCustomFieldsModal = true;
  }

  onDismissCustomFieldsModal() {
    this.isShowingCustomFieldsModal = false;
  }

  openTeamSelector() {
    this.searchablePopoverService.slackTeams({
      event,
      callback: (_id: string, _name: string, team: any) => {
        this.slackTeams.push(team);
      },
    });
  }

  async updateDefaultCustomFields() {
    this.isSavingCustomFields = true;
    await this.authService.updateTenant({
      customCustomerFields: this.customFieldOptions,
    });
    this.isSavingCustomFields = false;
    this.isShowingCustomFieldsModal = false;

    this.customFieldValues = generateCustomFieldsArray(this.customFieldOptions ?? [], this.customFieldValues ?? [], {});
  }

  addDomain() {
    if (!this.emailDomainToAdd || !this.emailDomainToAdd.includes('.')) {
      return;
    }

    if (!this.emailsAndDomains) {
      this.emailsAndDomains = [];
    }

    const domains = this.emailDomainToAdd
      .toLowerCase()
      .split(',')
      .map((domain) => domain.trim());

    this.emailsAndDomains.push(...domains);
    this.emailDomainToAdd = '';
  }

  deleteDomain(index: number) {
    this.emailsAndDomains.splice(index, 1);
  }

  removeTeam(index: number) {
    this.slackTeams.splice(index, 1);
  }

  async uploadIcon() {
    const modal = await this.modalCtrl.create({
      component: ImageModalComponent,
      componentProps: {
        searchValue: '',
        helperText: '',
        aspectRatio: 1,
      },
    });
    await modal.present();

    const { data } = await modal.onWillDismiss();

    // if backdrop is clicked and no data passed
    if (!data || !data.uploadFile) {
      return;
    }

    this.uploadService
      .upload([data.uploadFile], `slack`, fileNameFromImageModalUpload(data.uploadFile))
      .then(() => {
        this.imageUrl = data.uploadFile.url;
      })
      .catch((err) => {
        this.msgSrvc.show(err.message);
      });
  }

  linkCrm() {
    this.searchablePopoverService.externalCrmAccounts({
      selectedAccountId: this.externalCrmMetadata?.id,
      event,
      callback: async (id: string, name: string) => {
        const alert = await this.alertCtrl.create({
          header: 'Confirm account linking',
          subHeader: `This will link the ${name} profile to the ${this.account.name} account`,
          buttons: [
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Confirm',
              handler: async () => {
                await this.accountsService.updatePartial(this.id, {
                  externalCrmMetadata: { id },
                });
                await this.loadAccount();
              },
            },
          ],
        });

        await alert.present();
      },
    });
  }

  onSupportStepsChange(supportSteps: any[]) {
    this.supportSteps = supportSteps;
  }

  onEnableAccountSLAChange() {
    if (!this.enableAccountSLA) {
      this.supportSteps = [];
    }
  }

  viewInInbox() {
    this.dismiss();
    const filter = [
      [
        { label: 'Account', id: 'accountId', icon: 'business', type: 'select' },
        [{ id: this.id, label: this.account.name }],
      ],
    ];
    this.navCtrl.navigateRoot(`${routes.DASHBOARD}/${routes.INBOX}/${INBOX_TYPES.ALL}`, {
      queryParams: {
        filters: JSON.stringify(filter),
      },
      queryParamsHandling: 'merge',
    });
  }
}
